import { DOCUMENT } from '@angular/common';
import { inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { Constants } from '../@classes/constants';

@Injectable({
  providedIn: 'root'
})
export class CanonicalService {
  private document = inject(DOCUMENT);
  private renderer: Renderer2;
  private readonly siteUrl = Constants.BASE_URL;

  constructor(rendererFactory: RendererFactory2) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  update(pathOrUrl: string): void {
    const canonicalHref = this.buildCanonicalHref(pathOrUrl);
    const canonicalLink = this.getOrCreateCanonicalLink();

    this.renderer.setAttribute(canonicalLink, 'href', canonicalHref);
  }

  private buildCanonicalHref(pathOrUrl: string): string {
    const cleanedPath = this.normalizePath(pathOrUrl);
    return new URL(cleanedPath, this.siteUrl).toString();
  }

  private normalizePath(pathOrUrl: string): string {
    if (!pathOrUrl) {
      return '/';
    }

    const normalizedInput = String(pathOrUrl).trim();

    if (/^https?:\/\//i.test(normalizedInput)) {
      return this.normalizePath(new URL(normalizedInput).pathname);
    }

    const pathWithoutQuery = normalizedInput.split('?')[0].split('#')[0] || '/';
    const normalizedPath = `/${pathWithoutQuery.replace(/^\/+/, '')}`.replace(/\/{2,}/g, '/');

    if (normalizedPath === '/') {
      return normalizedPath;
    }

    return normalizedPath.replace(/\/+$/, '');
  }

  private getOrCreateCanonicalLink(): HTMLLinkElement {
    const existingCanonicalLink = this.document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;

    if (existingCanonicalLink) {
      return existingCanonicalLink;
    }

    const canonicalLink = this.renderer.createElement('link') as HTMLLinkElement;
    this.renderer.setAttribute(canonicalLink, 'rel', 'canonical');
    this.renderer.appendChild(this.document.head, canonicalLink);

    return canonicalLink;
  }
}
